import { AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import type { Milestone } from "@/hooks/use-milestones";
import type { Project } from "@/hooks/use-projects";

function formatCurrencyCompact(value: number): string {
  if (value >= 1_000_000_000) {
    return `$${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }
  return `$${value.toFixed(0)}`;
}

/** Matches the at-risk heuristic from MilestonesTab.tsx:81-88. */
function isAtRisk(m: Milestone): boolean {
  if (m.actualDate) return false;
  return Boolean(m.forecastDate && m.contractDate && m.forecastDate > m.contractDate);
}

interface ProjectsKpiCardsProps {
  projects: Project[];
  milestones: Milestone[];
}

export function ProjectsKpiCards({ projects, milestones }: ProjectsKpiCardsProps) {
  const totalContractValue = projects.reduce((sum, p) => {
    const n = p.contractValue ? Number(p.contractValue) : 0;
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);

  const activeCount = projects.filter((p) => p.status === "active").length;
  const inactiveCount = projects.length - activeCount;

  const atRiskCount = milestones.filter(isAtRisk).length;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <Card className="px-5 py-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Total Contract Value
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight">
          {formatCurrencyCompact(totalContractValue)}
        </p>
        <p className="mt-0.5 text-sm text-muted-foreground">Across {projects.length} projects</p>
      </Card>

      <Card className="px-5 py-3">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Active Projects
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight text-blue-600">{activeCount}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">{inactiveCount} inactive</p>
      </Card>

      <Card className="px-5 py-3">
        <div className="flex items-start justify-between">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            At-Risk Milestones
          </p>
          {atRiskCount > 0 ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : null}
        </div>
        <p className="mt-1 text-2xl font-bold tracking-tight text-amber-600">{atRiskCount}</p>
        <p className="mt-0.5 text-sm text-muted-foreground">Require attention</p>
      </Card>
    </div>
  );
}
